import { defineComponent as _defineComponent } from 'vue'
import CcModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcModal.ts.vue';
import configFile from '@sh/components/TrafficSources/config';
import OptimizerIcon from '@sh/components/Utils/OptimizerIcon.ts.vue';
import { api } from '@sh/services/api';
import { Account, AccountSettings, ComplianceProgram, SearchFeed } from '@sh/types';
import { computed, ref } from 'vue';

interface Dropdown<Option> {
  selected: NullableType<Option>;
  options: Option[];
}

interface Emits {
  (event: 'onNotification', message: string, type: string): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TransparencyProgramModal.ts',
  emits: ["onNotification"],
  setup(__props, { expose, emit: emits }: { emit: ({
  (event: 'onNotification', message: string, type: string): void;
}), expose: any, slots: any, attrs: any }) {



const isModalOpen = ref(false);
const isLoading = ref(false);

const userSettings = ref<NullableType<SearchFeed.UserResponse>>(null);
const config = ref<NullableType<Awaited<ReturnType<typeof configFile.getConfig>>>>(null);
const isFormActive = ref(false);
const accountId = ref<string>('');
const accountSettings = ref<NullableType<AccountSettings>>(null);
const emailDropdown = ref<Dropdown<SelectOption<string>>>({
  selected: null,
  options: [],
});
const searchFeedDropdown = ref<Dropdown<SelectOption<string>>>({
  selected: null,
  options: [],
});

function validateData(data: Partial<ComplianceProgram>): data is ComplianceProgram {
  if (!data.is_complianced) {
    return true;
  }

  if (!data.type) {
    return false;
  }

  if (!data.email) {
    return false;
  }

  return true;
}

const complianceProgram = computed<NullableType<ComplianceProgram>>(() => {
  const data = {
    email: emailDropdown.value.selected?.value,
    is_complianced: isFormActive.value,
    type: searchFeedDropdown.value.selected?.value,
  };

  if (validateData(data)) {
    return data;
  }

  return null;
});

const save = computed(() => {
  return {
    label: 'Save',
    disabledMessage: complianceProgram.value ? '' : 'Please select search feed provider and email to save changes.',
  };
});

function setOptions() {
  searchFeedDropdown.value.options = Object.values(userSettings.value?.settings.compliance ?? {}).map((item) => ({
    content: item.type,
    value: item.type,
  }));

  if (userSettings.value?.settings.compliance?.[searchFeedDropdown.value.selected?.value ?? '']?.email) {
    emailDropdown.value.options = userSettings.value.settings.compliance[
      searchFeedDropdown.value.selected?.value ?? ''
    ].email.map((email) => ({
      content: email,
      value: email,
    }));
  } else {
    emailDropdown.value.options = [];
  }
}

async function updateComplianceData() {
  if (!accountSettings.value || !complianceProgram.value) {
    return;
  }

  try {
    accountSettings.value = {
      ...accountSettings.value,
      complianceProgram: complianceProgram.value,
    };
    await config.value?.api.trafficSources.update(accountId.value, { settings: accountSettings.value });
    emits('onNotification', 'Transparency Program updated successfully', 'success');
  } catch {
    emits('onNotification', 'Failed to update Transparency Program', 'error');
  }
}

function onSearchFeedActiveChange(value: boolean) {
  isFormActive.value = value;
  searchFeedDropdown.value.selected = null;
  emailDropdown.value.selected = null;
}

async function onSearchFeedChange(event: SelectOption<string>) {
  searchFeedDropdown.value.selected = event;
  emailDropdown.value.selected = null;
  emailDropdown.value.options =
    userSettings.value?.settings.compliance?.[event.value].email.map((email) => ({
      content: email,
      value: email,
    })) ?? [];
}

function onModalClose() {
  isModalOpen.value = false;
  isLoading.value = false;
  accountId.value = '';
  accountSettings.value = null;
  emailDropdown.value.selected = null;
  searchFeedDropdown.value.selected = null;
  isFormActive.value = false;
}

async function show(account: Account) {
  isModalOpen.value = true;
  isLoading.value = true;

  try {
    userSettings.value = await api.trafficSources.users();
    config.value = await configFile.getConfig();

    accountId.value = account.id.toString();
    accountSettings.value = account.settings;

    if (account?.settings?.complianceProgram?.type || account?.settings?.complianceProgram?.email) {
      searchFeedDropdown.value.selected = {
        content: account.settings.complianceProgram.type,
        value: account.settings.complianceProgram.type,
      };

      emailDropdown.value.selected = {
        content: account.settings.complianceProgram.email,
        value: account.settings.complianceProgram.email,
      };

      isFormActive.value = true;
    }

    setOptions();
  } catch {
    emits('onNotification', 'Failed to fetch Transparency Program data', 'error');
    onModalClose();
  }

  isLoading.value = false;
}

async function onSaveClick() {
  isLoading.value = true;
  await updateComplianceData();
  onModalClose();
}

expose({
  show,
});

return { __sfc: true,emits, isModalOpen, isLoading, userSettings, config, isFormActive, accountId, accountSettings, emailDropdown, searchFeedDropdown, validateData, complianceProgram, save, setOptions, updateComplianceData, onSearchFeedActiveChange, onSearchFeedChange, onModalClose, show, onSaveClick, CcModal, OptimizerIcon }
}

})